import { Tooltip } from '@clickcease/cc-client-components-lib'
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import mapDataWorld from '@highcharts/map-collection/custom/world.geo.json'
import Highcharts from 'highcharts'
import highchartsMap from 'highcharts/modules/map'
import HighchartsReact from 'highcharts-react-official'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BlockByCountryEntry, NormalizedSummaryData } from '../../../../../hooks/api/BMDataTypes'

import { HeatMapWrapper, HeatMapTitle, ChartContainer, TooltipStyle } from './HeatMap.styled'
import { IconProp } from '@fortawesome/fontawesome-svg-core'

if (typeof Highcharts === 'object') {
  highchartsMap(Highcharts)
}

interface HeatMapProps {
  summary: NormalizedSummaryData
}

export const HeatMap: React.FC<HeatMapProps> = ({ summary }) => {
  const { t } = useTranslation('botMitigation')

  const blocksByCountry = useMemo<BlockByCountryEntry[]>(() => {
    return summary.rawData?.blocksByCountry ?? []
  }, [summary.rawData])

  const mapData = useMemo(() => {
    return blocksByCountry.map((country) => {
      return {
        'hc-key': country.country.toLowerCase(),
        value: country.totalBlocked,
        invalidRate: country.invalidRate,
        name: country.country,
        iso2: country.country,
      }
    })
  }, [blocksByCountry])

  const chartOptions = useMemo(() => {
    return {
      chart: {
        map: mapDataWorld,
        backgroundColor: 'transparent',
        height: 450,
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: true,
        title: {
          text: t('heatMap.numberOfBlocks'),
        },
      },
      mapNavigation: {
        enabled: true,
        buttonOptions: {
          verticalAlign: 'bottom',
        },
      },
      colorAxis: {
        min: 0,
        stops: [
          [0, '#FDECF2'],
          [0.5, '#F58BB0'],
          [1, '#D10068'],
        ],
      },
      tooltip: {
        headerFormat: '',
        pointFormat: `<b>{point.name}</b><br>${t(
          'heatMap.numberOfBlocks'
        )}: {point.value}<br>Invalid Rate: {point.invalidRate:.2f}%`,
      },
      series: [
        {
          data: mapData,
          name: t('heatMap.numberOfBlocks'),
          states: {
            hover: {
              color: '#ADD8E6',
            },
          },
          dataLabels: {
            enabled: false,
            format: '{point.name}',
          },
        },
      ],
    }
  }, [mapData, t])

  return (
    <HeatMapWrapper>
      <HeatMapTitle>
        <span>{t('heatMap.title')}</span>
        <Tooltip
          tooltiptContent={t('heatMap.tooltip')}
          constentStyle={{ width: '270px', fontSize: '12px' }}
          side="top"
        >
          <FontAwesomeIcon icon={faInfoCircle as IconProp} style={TooltipStyle} />
        </Tooltip>
      </HeatMapTitle>
      <ChartContainer>
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          constructorType={'mapChart'}
        />
      </ChartContainer>
    </HeatMapWrapper>
  )
}
